<template>
  <section id="showcase" class="showcase-section">
    <div class="showcase-container">

      <!-- Shoplinks showcase -->
      <div class="showcase-row">
        <div class="showcase-visual" data-reveal="left">
          <div class="phone-frame">
            <div class="phone-notch"></div>
            <div class="phone-content">
              <div class="mock-artwork-sm"></div>
              <div class="mock-title-sm"></div>
              <div class="mock-artist-sm"></div>
              <div class="mock-platforms-list">
                <div v-for="platform in ['Spotify', 'Apple Music', 'Tidal', 'Deezer', 'Amazon']" :key="platform" class="mock-platform-btn">
                  <span class="platform-icon-dot"></span>
                  <span class="platform-name">{{ platform }}</span>
                  <span class="platform-arrow">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="9 18 15 12 9 6" />
                    </svg>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="showcase-text" data-reveal="right">
          <span class="section-label cyan">{{ $t('landing.shoplinks_label') }}</span>
          <h2 class="section-title">
            {{ $t('landing.shoplinks_title_pre') }}
            <span class="gradient-text">{{ $t('landing.shoplinks_title_highlight') }}</span>
          </h2>
          <p class="showcase-desc">{{ $t('landing.shoplinks_desc') }}</p>
        </div>
      </div>

      <!-- Download Gates showcase -->
      <div class="showcase-row reverse">
        <div class="showcase-text" data-reveal="left">
          <span class="section-label coral">{{ $t('landing.gates_label') }}</span>
          <h2 class="section-title">
            {{ $t('landing.gates_title_pre') }}
            <span class="gradient-text-coral">{{ $t('landing.gates_title_highlight') }}</span>
          </h2>
          <p class="showcase-desc">{{ $t('landing.gates_desc') }}</p>
        </div>

        <div class="showcase-visual" data-reveal="right">
          <div class="gate-card">
            <div class="gate-header">
              <div class="gate-artwork"></div>
              <div class="gate-info">
                <div class="gate-title-bar"></div>
                <div class="gate-subtitle-bar"></div>
              </div>
            </div>
            <div class="gate-tasks">
              <div v-for="(task, i) in gateTasks" :key="i" class="gate-task" :class="{ completed: task.done }">
                <span class="task-check">
                  <svg v-if="task.done" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span class="task-label">{{ task.label }}</span>
              </div>
            </div>
            <div class="gate-download">
              <div class="download-btn-mock">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                Download
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script setup>
const gateTasks = [
    { label: 'Follow on Spotify', done: true },
    { label: 'Save to Library', done: true },
    { label: 'Follow on SoundCloud', done: false },
]
</script>

<style scoped>
/* Mobile-first. Ladder: 480 / 640 / 768 / 1024 / 1280 (variables.css). */
.showcase-section {
  position: relative;
  padding: clamp(32px, 5vw, 60px) 0 var(--landing-section-pad);
}

.showcase-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 var(--space-6);
  display: flex;
  flex-direction: column;
  gap: clamp(72px, 10vw, 120px);
}

.showcase-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 40px;
  align-items: center;
}

@media (min-width: 768px) {
  .showcase-row {
    grid-template-columns: 1fr 1fr;
    gap: 48px;
  }

  .showcase-row.reverse .showcase-text {
    order: -1;
  }
}

@media (min-width: 1024px) {
  .showcase-row {
    gap: 80px;
  }
}

/* Text */
.section-label {
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  margin-bottom: var(--space-4);
}

.section-label.cyan { color: var(--primary); }
.section-label.coral { color: var(--secondary); }

.section-title {
  font-family: var(--font-heading);
  font-size: var(--landing-title-size);
  font-weight: 800;
  line-height: 1.1;
  color: #fff;
  margin-bottom: var(--space-6);
  text-wrap: balance;
}

.gradient-text {
  background: linear-gradient(135deg, var(--primary), var(--secondary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.gradient-text-coral {
  background: linear-gradient(135deg, var(--secondary), var(--primary));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.showcase-desc {
  font-size: 1.05rem;
  color: var(--text-muted);
  line-height: 1.7;
  max-width: 440px;
}

/* Phone frame mockup */
.showcase-visual {
  display: flex;
  justify-content: center;
}

.phone-frame {
  width: min(260px, 100%);
  border: 2px solid var(--border);
  border-radius: 32px;
  background: var(--surface-color);
  padding: 12px 16px 24px;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
}

.phone-notch {
  width: 80px;
  height: 4px;
  background: var(--border);
  border-radius: 4px;
  margin: 0 auto 16px;
}

.mock-artwork-sm {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(0, 242, 255, 0.3), rgba(255, 107, 107, 0.3));
  margin-bottom: 12px;
}

.mock-title-sm {
  height: 10px;
  width: 65%;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 5px;
  margin-bottom: 6px;
}

.mock-artist-sm {
  height: 8px;
  width: 40%;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
  margin-bottom: 16px;
}

.mock-platforms-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mock-platform-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  transition: all 0.2s ease;
}

.mock-platform-btn:hover {
  background: rgba(0, 242, 255, 0.05);
  border-color: rgba(0, 242, 255, 0.2);
}

.platform-icon-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 242, 255, 0.15);
  flex-shrink: 0;
}

.platform-name {
  flex: 1;
  font-size: 0.75rem;
  color: var(--text-muted);
  font-weight: 500;
}

.platform-arrow {
  color: var(--text-dim);
}

/* Gate card mockup */
.gate-card {
  width: min(300px, 100%);
  background: var(--surface-color);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  padding: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  position: relative;
}

.gate-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.gate-artwork {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.4), rgba(0, 242, 255, 0.4));
  flex-shrink: 0;
}

.gate-info {
  flex: 1;
}

.gate-title-bar {
  height: 10px;
  width: 80%;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 5px;
  margin-bottom: 6px;
}

.gate-subtitle-bar {
  height: 8px;
  width: 50%;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 4px;
}

.gate-tasks {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}

.gate-task {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border);
  border-radius: 10px;
  transition: all 0.2s ease;
}

.gate-task.completed {
  border-color: rgba(0, 242, 255, 0.2);
  background: rgba(0, 242, 255, 0.03);
}

.task-check {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--primary);
}

.gate-task.completed .task-check {
  border-color: var(--primary);
  background: rgba(0, 242, 255, 0.15);
}

.task-label {
  font-size: 0.8rem;
  color: var(--text-muted);
  font-weight: 500;
}

.gate-download {
  padding-top: 16px;
  border-top: 1px solid var(--border);
}

.download-btn-mock {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  background: rgba(255, 107, 107, 0.15);
  border: 1px solid rgba(255, 107, 107, 0.3);
  color: var(--secondary);
  font-size: 0.85rem;
  font-weight: 600;
}

/* pulsing glow is desktop-only decoration (infinite box-shadow repaint) */
@media (min-width: 768px) {
  .download-btn-mock {
    animation: downloadGlow 3s ease-in-out infinite;
  }
}

@keyframes downloadGlow {
  0%, 100% { box-shadow: 0 0 10px rgba(255, 107, 107, 0.1); }
  50% { box-shadow: 0 0 25px rgba(255, 107, 107, 0.25); }
}

</style>
